import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { 
  fetchDashboardData, 
  fetchProfileData 
} from '../redux/accountSlice';

// Import Tabs
import DashboardTab from '../components/account/DashboardTab';
import OrdersTab from '../components/account/OrdersTab';
import BillsTab from '../components/account/BillsTab';
import AddressesTab from '../components/account/AddressesTab';
import ProfileTab from '../components/account/ProfileTab';
import SecurityTab from '../components/account/SecurityTab';

// Import Layout Components
import AccountSidebar from '../components/account/AccountSidebar';
import AccountMobileMenu from '../components/account/AccountMobileMenu';

import '../styles/account.css';

const Account = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Modal states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);

  // Redux state
  const { dashboard, loading, addresses } = useSelector((state) => state.account);

  useEffect(() => {
    // Fetch initial profile & dashboard stats
    dispatch(fetchDashboardData());
    dispatch(fetchProfileData());
  }, [dispatch]);

  useEffect(() => {
    const handleToggle = () => {
      setIsMobileMenuOpen((prev) => !prev);
    };
    window.addEventListener('toggleAccountDrawer', handleToggle);
    return () => {
      window.removeEventListener('toggleAccountDrawer', handleToggle);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user) return null;

  // Render current tab component
  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardTab
            dashboardData={dashboard}
            addresses={addresses}
            loading={loading}
            setActiveTab={setActiveTab}
            onViewOrderDetails={setSelectedOrder}
            onViewBill={setSelectedBill}
          />
        );
      case 'orders':
        return <OrdersTab onViewOrderDetails={setSelectedOrder} />;
      case 'bills':
        return <BillsTab onViewBill={setSelectedBill} />;
      case 'addresses':
        return <AddressesTab token={user.token} />;
      case 'profile':
        return <ProfileTab token={user.token} />;
      case 'security':
        return <SecurityTab token={user.token} />;
      default:
        return (
          <DashboardTab
            dashboardData={dashboard}
            addresses={addresses}
            loading={loading}
            setActiveTab={setActiveTab}
            onViewOrderDetails={setSelectedOrder}
            onViewBill={setSelectedBill}
          />
        );
    }
  };

  const getStatusClass = (statusName) => {
    switch (statusName) {
      case 'Pending': return 'pending';
      case 'Verified': return 'confirmed';
      case 'Processing': return 'packed';
      case 'Shipped': return 'shipped';
      case 'Delivered': return 'delivered';
      case 'Cancelled': return 'cancelled';
      default: return 'pending';
    }
  };

  return (
    <>
      {/* Mobile view Header & Drawer Navigation */}
      <AccountMobileMenu
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isOpen={isMobileMenuOpen}
        setIsOpen={setIsMobileMenuOpen}
        onLogout={handleLogout}
        profile={dashboard.profile}
      />

      <div className="account-container">
        {/* Desktop Sidebar menu */}
        <AccountSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onLogout={handleLogout}
        />

        {/* Tab display pane */}
        <div className="account-main-content">
          {renderTabContent()}
        </div>
      </div>

      {/* 1. ORDER DETAILS MODAL */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order Specifications</h3>
              <button onClick={() => setSelectedOrder(null)} className="modal-close-btn">&times;</button>
            </div>
            <div className="modal-body">
              
              {/* Order Meta details */}
              <div className="order-details-meta">
                <div className="order-details-meta-item">
                  <span className="order-details-meta-label">Order Reference</span>
                  <span className="order-details-meta-value">
                    {selectedOrder.orderNumber || `Order #${selectedOrder._id.substring(18)}`}
                  </span>
                </div>
                <div className="order-details-meta-item">
                  <span className="order-details-meta-label">Placed On</span>
                  <span className="order-details-meta-value">
                    {selectedOrder.createdAt 
                      ? new Date(selectedOrder.createdAt).toLocaleDateString() 
                      : ''}
                  </span>
                </div>
                <div className="order-details-meta-item">
                  <span className="order-details-meta-label">Status</span>
                  <div>
                    <span className={`status-badge ${getStatusClass(selectedOrder.status)}`} style={{ display: 'inline-block' }}>
                      {selectedOrder.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment and Tracking details */}
              <div className="order-details-meta" style={{ marginTop: '15px' }}>
                <div className="order-details-meta-item">
                  <span className="order-details-meta-label">Payment Method</span>
                  <span className="order-details-meta-value">
                    {selectedOrder.paymentMethod === 'full_prepaid' ? 'Full Prepaid' : '20% Advance'}
                  </span>
                </div>
                <div className="order-details-meta-item">
                  <span className="order-details-meta-label">Payment Status</span>
                  <span className="order-details-meta-value">
                    <span style={{
                      color: selectedOrder.paymentStatus === 'Paid' ? '#10b981' :
                             selectedOrder.paymentStatus === 'Partial' ? '#3b82f6' :
                             selectedOrder.paymentStatus === 'Failed' ? '#ef4444' :
                             selectedOrder.paymentStatus === 'Refunded' ? '#ec4899' : '#f59e0b',
                      fontWeight: '600'
                    }}>
                      {selectedOrder.paymentStatus || 'Pending'}
                    </span>
                  </span>
                </div>
                {selectedOrder.trackingNumber && (
                  <div className="order-details-meta-item">
                    <span className="order-details-meta-label">Tracking Number</span>
                    <span className="order-details-meta-value" style={{ color: '#ff7a00', fontWeight: '600' }}>
                      {selectedOrder.trackingNumber}
                    </span>
                  </div>
                )}
              </div>

              {/* Shipping Address */}
              <div className="modal-section-title" style={{ marginTop: '20px' }}>Delivery Address</div>
              <div style={{ background: '#1c1c1e', padding: '15px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.03)', color: '#d4d4d8', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '20px' }}>
                <strong>{selectedOrder.address?.contactPerson || selectedOrder.user?.name}</strong><br />
                {selectedOrder.address?.shopName && <>{selectedOrder.address.shopName}<br /></>}
                {selectedOrder.address?.street}<br />
                {selectedOrder.address?.city}, {selectedOrder.address?.state} - {selectedOrder.address?.postalCode}<br />
                {selectedOrder.address?.country || 'India'}<br />
                <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>Phone: {selectedOrder.address?.phone}</span>
              </div>

              {/* Order status history timeline */}
              {selectedOrder.statusHistory && selectedOrder.statusHistory.length > 0 && (
                <>
                  <div className="modal-section-title">Status Timeline</div>
                  <div className="order-timeline-list">
                    {selectedOrder.statusHistory.map((node, index) => (
                      <div 
                        key={index} 
                        className={`order-timeline-node ${index === selectedOrder.statusHistory.length - 1 ? 'active' : ''}`}
                      >
                        <span className="timeline-status">{node.status}</span>
                        <span className="timeline-date">
                          {new Date(node.updatedAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Order Items list */}
              <div className="modal-section-title">Order Items</div>
              <div className="order-items-scroll-list">
                {selectedOrder.items && selectedOrder.items.map((item, idx) => (
                  <div key={idx} className="order-item-detail-row">
                    <div className="order-item-prod-info">
                      <img 
                        src={item.primaryImage || '/RegardTradersLogo.png'} 
                        alt={item.productName} 
                        className="order-item-img"
                        onError={(e) => { e.target.src = '/RegardTradersLogo.png'; }}
                      />
                      <div className="order-item-name-qty">
                        <span className="order-item-name">{item.productName || 'Wholesale Product'}</span>
                        <span className="order-item-qty">Quantity: {item.qty} pcs</span>
                      </div>
                    </div>
                    <span className="order-item-price">
                      ₹{item.price ? item.price.toLocaleString('en-IN') : '0'}
                    </span>
                  </div>
                ))}
              </div>

              {/* Applied Coupon Info Badge (Right Aligned after Order Items) */}
              {selectedOrder.coupon && selectedOrder.coupon.code && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px', marginBottom: '4px' }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(16, 185, 129, 0.08)',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    borderRadius: '20px',
                    padding: '6px 14px',
                    fontSize: '0.85rem',
                    color: '#10b981',
                    fontWeight: '600'
                  }}>
                    <Tag size={14} />
                    <span>Applied Coupon: <strong>{selectedOrder.coupon.code}</strong> ({selectedOrder.coupon.discountType === 'FixedAmount' ? `₹${selectedOrder.coupon.discountValue} Off` : `${selectedOrder.coupon.discountValue || selectedOrder.coupon.discountPercentage}% Off`})</span>
                  </div>
                </div>
              )}

              {/* Order total sum breakdown */}
              <div style={{ background: '#1c1c1e', padding: '15px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.03)', marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                  <span>Items Subtotal:</span>
                  <span>₹{selectedOrder.subtotal?.toLocaleString('en-IN') || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                  <span>Packing Charges:</span>
                  <span>₹{selectedOrder.packingCharge?.toLocaleString('en-IN') || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                  <span>Shipping Charges:</span>
                  <span>₹{selectedOrder.shippingCharge?.toLocaleString('en-IN') || 0}</span>
                </div>
                {selectedOrder.coupon && selectedOrder.coupon.code && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#10b981' }}>
                    <span>Coupon Amount:</span>
                    <span style={{ fontWeight: '700' }}>-₹{selectedOrder.coupon.discountAmount?.toLocaleString('en-IN') || 0}</span>
                  </div>
                )}
                {selectedOrder.paymentMethod === 'full_prepaid' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#10b981' }}>
                    <span>Prepaid Discount:</span>
                    <span>-₹200.00</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', color: '#ff7a00', fontWeight: '700', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '8px', marginTop: '4px' }}>
                  <span>Grand Total:</span>
                  <span>₹{selectedOrder.totalAmount?.toLocaleString('en-IN') || 0}</span>
                </div>
              </div>

            </div>
            <div className="modal-footer">
              <button onClick={() => setSelectedOrder(null)} className="btn">
                Close Specifications
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. BILL PREVIEW OVERLAY MODAL */}
      {selectedBill && (
        <div className="modal-overlay" onClick={() => setSelectedBill(null)}>
          <div className="modal-container" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedBill.invoiceNumber} - Invoice Preview</h3>
              <button onClick={() => setSelectedBill(null)} className="modal-close-btn">&times;</button>
            </div>
            <div className="modal-body">
              <div className="bill-full-preview-container">
                <img src={selectedBill.imageUrl} alt={selectedBill.invoiceNumber} />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setSelectedBill(null)} className="btn">
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Account;
